import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Checkbox } from "../../../components/ui/checkbox";
import { Badge } from "../../../components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Separator } from "../../../components/ui/separator";
import { Users, X, Filter, Plus, Trash2, User } from "lucide-react";
import { toast } from "sonner";
import {
  CreateGroupDto,
  UserFilters,
  PreviewUser,
  PreviewUsersResponse,
} from "../../../api/notificationApi";
import NotificationApiService from "../../../api/notificationApi";
import { RankSelect } from "../../../components/ui/rank-select";

interface CreateGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateGroup: (dto: CreateGroupDto) => Promise<{
    success?: boolean;
    message?: string;
    error?: string;
    statusCode?: number;
    data?: any;
  }>;
}

// Real data from API
const ROLES = ["SEAFARER", "ADMIN", "SUPER_ADMIN"];
const GENDERS = ["MALE", "FEMALE", "OTHER"];

const SHIPS = [
  "MV Pacific Voyager",
  "MV Ocean Explorer",
  "MV Sea Carrier",
  "MV Global Trader",
  "MV Maritime Star",
  "MV Coastal Runner",
  "MV Deep Sea",
  "MV Horizon",
];

export default function CreateGroupModal({
  isOpen,
  onClose,
  onCreateGroup,
}: CreateGroupModalProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);

  // Filters
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedRanks, setSelectedRanks] = useState<string[]>([]);
  const [selectedShips, setSelectedShips] = useState<string[]>([]);
  const [selectedGenders, setSelectedGenders] = useState<string[]>([]);
  const [ageMin, setAgeMin] = useState<number | undefined>();
  const [ageMax, setAgeMax] = useState<number | undefined>();
  const [lastLoginDays, setLastLoginDays] = useState<number | undefined>();
  const [customQuery, setCustomQuery] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [previewUsers, setPreviewUsers] = useState<PreviewUser[]>([]);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setName("");
      setDescription("");
      setIsActive(true);
      setSelectedRoles([]);
      setSelectedRanks([]);
      setSelectedShips([]);
      setSelectedGenders([]);
      setAgeMin(undefined);
      setAgeMax(undefined);
      setLastLoginDays(undefined);
      setCustomQuery("");
      setPreviewUsers([]);
      setShowPreview(false);
    }
  }, [isOpen]);

  const buildFilters = (): UserFilters => {
    const filters: UserFilters = {};

    if (selectedRoles.length > 0) filters.roles = selectedRoles;
    if (selectedRanks.length > 0) filters.ranks = selectedRanks;
    if (selectedShips.length > 0) filters.shipNames = selectedShips;
    if (selectedGenders.length > 0) filters.gender = selectedGenders;
    if (ageMin !== undefined && ageMin > 0) filters.ageRange = { min: ageMin };
    if (ageMax !== undefined && ageMax > 0) {
      if (filters.ageRange) {
        filters.ageRange.max = ageMax;
      } else {
        filters.ageRange = { max: ageMax };
      }
    }
    if (lastLoginDays !== undefined && lastLoginDays > 0)
      filters.lastLoginDays = lastLoginDays;
    if (customQuery.trim()) filters.customQuery = customQuery.trim();

    return filters;
  };

  const handlePreviewUsers = async () => {
    const filters = buildFilters();

    if (Object.keys(filters).length === 0) {
      toast.error("Please set at least one filter criteria");
      return;
    }

    setIsPreviewLoading(true);
    try {
      const response = await NotificationApiService.previewUsers(filters);
      if (response.success && response.data) {
        setPreviewUsers(response.data.usersPreview);
        setShowPreview(true);
        toast.success(
          `Found ${response.data.count} users matching the criteria`
        );
      } else {
        toast.error(response.message || "Failed to preview users");
      }
    } catch (error) {
      console.error("Error previewing users:", error);
      toast.error("Failed to preview users");
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error("Group name is required");
      return;
    }

    // Validate age range if both are provided
    if (ageMin !== undefined && ageMax !== undefined && ageMin > ageMax) {
      toast.error("Minimum age cannot be greater than maximum age");
      return;
    }

    // Validate last login days
    if (
      lastLoginDays !== undefined &&
      (lastLoginDays < 1 || lastLoginDays > 365)
    ) {
      toast.error("Last login days must be between 1 and 365");
      return;
    }

    const filters = buildFilters();

    // Check if any filters are set
    if (Object.keys(filters).length === 0) {
      toast.error("Please set at least one filter criteria");
      return;
    }

    setIsLoading(true);

    try {
      const dto: CreateGroupDto = {
        name: name.trim(),
        description: description.trim() || undefined,
        filters,
        isActive,
      };
      await onCreateGroup(dto);
    } catch (error) {
      console.error("Error creating group:", error);
      if (error.response?.data?.message) {
        toast.error(`Failed to create group: ${error.response.data.message}`);
      } else if (error.message) {
        toast.error(`Failed to create group: ${error.message}`);
      } else {
        toast.error("Failed to create group");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const addToList = (
    list: string[],
    value: string,
    setter: (list: string[]) => void
  ) => {
    if (!list.includes(value)) {
      setter([...list, value]);
    }
  };

  const removeFromList = (
    list: string[],
    value: string,
    setter: (list: string[]) => void
  ) => {
    setter(list.filter((item) => item !== value));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create User Group</DialogTitle>
          <DialogDescription>
            Create a group of users based on filter criteria for targeted
            notifications
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Group Info and Filters */}
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Group Name</Label>
                <Input
                  placeholder="Enter group name..."
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>Description (Optional)</Label>
                <Textarea
                  placeholder="Describe this group..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="active"
                  checked={isActive}
                  onCheckedChange={(checked) => setIsActive(checked === true)}
                />
                <Label htmlFor="active">Active group</Label>
              </div>
            </div>

            <Separator />

            {/* Filter Criteria */}
            <div className="space-y-4 w-full">
              <h3 className="text-lg font-medium">Filter Criteria</h3>

              <div className="flex items-center justify-between">
                {/* Roles */}
                <div className="space-y-2">
                  <Label>Roles</Label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {selectedRoles.map((role) => (
                      <div
                        key={role}
                        className="flex items-center gap-1 text-[11px]"
                      >
                        {role}
                        <X
                          className="h-3 w-3 cursor-pointer hover:text-red-500 z-100"
                          onClick={() =>
                            removeFromList(
                              selectedRoles,
                              role,
                              setSelectedRoles
                            )
                          }
                        />
                      </div>
                    ))}
                  </div>
                  <Select
                    onValueChange={(value) =>
                      addToList(selectedRoles, value, setSelectedRoles)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Add role..." />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.filter(
                        (role) => !selectedRoles.includes(role)
                      ).map((role) => (
                        <SelectItem key={role} value={role}>
                          {role}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Ranks */}
                <div className="space-y-2">
                  <Label>Ranks</Label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {selectedRanks.map((rank) => (
                      <div
                        key={rank}
                        className="flex items-center gap-1 text-[11px]"
                      >
                        {rank}
                        <X
                          className="h-3 w-3 cursor-pointer hover:text-red-500"
                          onClick={() =>
                            removeFromList(
                              selectedRanks,
                              rank,
                              setSelectedRanks
                            )
                          }
                        />
                      </div>
                    ))}
                  </div>
                  <RankSelect
                    value=""
                    onValueChange={(value) =>
                      addToList(selectedRanks, value, setSelectedRanks)
                    }
                    placeholder="Search and add rank…"
                    exclude={selectedRanks}
                  />
                </div>

                {/* Ships */}
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-2 w-full">
                  <Label>Ships</Label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {selectedShips.map((ship) => (
                      <div
                        key={ship}
                        className="flex items-center gap-1 text-[11px]"
                      >
                        {ship}
                        <X
                          className="h-3 w-3 cursor-pointer hover:text-red-500"
                          onClick={() =>
                            removeFromList(
                              selectedShips,
                              ship,
                              setSelectedShips
                            )
                          }
                        />
                      </div>
                    ))}
                  </div>
                  <Select
                    onValueChange={(value) =>
                      addToList(selectedShips, value, setSelectedShips)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Add ship..." />
                    </SelectTrigger>
                    <SelectContent>
                      {SHIPS.filter(
                        (ship) => !selectedShips.includes(ship)
                      ).map((ship) => (
                        <SelectItem key={ship} value={ship}>
                          {ship}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Gender */}
                <div className="space-y-2">
                  <Label>Gender</Label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {selectedGenders.map((gender) => (
                      <div
                        key={gender}
                        className="flex items-center gap-1 text-[11px]"
                      >
                        {gender}
                        <X
                          className="h-3 w-3 cursor-pointer hover:text-red-500"
                          onClick={() =>
                            removeFromList(
                              selectedGenders,
                              gender,
                              setSelectedGenders
                            )
                          }
                        />
                      </div>
                    ))}
                  </div>
                  <Select
                    onValueChange={(value) =>
                      addToList(selectedGenders, value, setSelectedGenders)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Add gender..." />
                    </SelectTrigger>
                    <SelectContent>
                      {GENDERS.filter(
                        (gender) => !selectedGenders.includes(gender)
                      ).map((gender) => (
                        <SelectItem key={gender} value={gender}>
                          {gender}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                {/* Age Range */}
                <div className="space-y-2">
                  <Label>Age Range</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      type="number"
                      placeholder="Min age"
                      value={ageMin || ""}
                      onChange={(e) =>
                        setAgeMin(
                          e.target.value ? Number(e.target.value) : undefined
                        )
                      }
                    />
                    <Input
                      type="number"
                      placeholder="Max age"
                      value={ageMax || ""}
                      onChange={(e) =>
                        setAgeMax(
                          e.target.value ? Number(e.target.value) : undefined
                        )
                      }
                    />
                  </div>
                </div>

                {/* Last Login */}
                <div className="space-y-2 pt-2">
                  <Label>Active in last X days</Label>
                  <Input
                    type="number"
                    placeholder="Days"
                    value={lastLoginDays || ""}
                    onChange={(e) =>
                      setLastLoginDays(
                        e.target.value ? Number(e.target.value) : undefined
                      )
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Group Summary */}
          <div className="space-y-4">
            {/* Preview Users */}

            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Preview Users ({previewUsers.length})
                  </div>
                  <div>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={handlePreviewUsers}
                      disabled={
                        isPreviewLoading ||
                        Object.keys(buildFilters()).length === 0
                      }
                    >
                      {isPreviewLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
                          Previewing Users...
                        </>
                      ) : (
                        <>
                          <Users className="h-4 w-4 mr-2" />
                          Preview Users (
                          {Object.keys(buildFilters()).length > 0
                            ? "Ready"
                            : "No Filters"}
                          )
                        </>
                      )}
                    </Button>
                  </div>
                </CardTitle>
                <div className="text-xs text-muted-foreground">
                  {previewUsers.length > 0 &&
                    `Total users matching criteria: ${previewUsers.length}`}
                </div>
              </CardHeader>
              <CardContent className="space-y-3 max-h-[40vh] overflow-y-auto">
                {isPreviewLoading ? (
                  <div className="flex items-center justify-center h-20">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    <span className="ml-2 text-sm text-muted-foreground">
                      Loading users...
                    </span>
                  </div>
                ) : previewUsers.length > 0 ? (
                  previewUsers.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center gap-3 p-2 border rounded-lg"
                    >
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                        <User className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">
                          {user.name ||
                            (user.firstName && user.lastName
                              ? `${user.firstName} ${user.lastName}`
                              : user.email.split("@")[0])}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {user.email}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {user.role && (
                          <Badge variant="outline" className="text-xs">
                            {user.role}
                          </Badge>
                        )}
                        {user.rankDesignation && (
                          <Badge variant="outline" className="text-xs">
                            {user.rankDesignation}
                          </Badge>
                        )}
                        {user.currentShipName && (
                          <Badge variant="secondary" className="text-xs">
                            {user.currentShipName}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-muted-foreground py-4">
                    <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No users found</p>
                    <p className="text-xs">
                      Try adjusting your filter criteria
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? "Creating..." : "Create Group"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
